import { getCommonPinningStyles } from '@repo/lib';
import { Cell, flexRender } from '@tanstack/react-table';
import { TableCell } from '@ui/components';
import { cn } from '@ui/lib/utils';
import { ChevronDownIcon, ChevronRightIcon } from 'lucide-react';
import { CSSProperties, HTMLProps, useMemo } from 'react';
import { useAppTableContext } from '../../lib/context/use-app-table-context';

interface DataTableDragCellProps<TData, TValue> {
  cell: Cell<TData, TValue>;
  style?: CSSProperties;
  //   styleCell?: CSSProperties;
  className?: HTMLProps<HTMLElement>['className'];
  //   onRowClick?: (data: TData, column: ColumnDef<TData, unknown>) => void;
  //   onDoubleClick?: (data: TData, column: ColumnDef<TData, unknown>) => void;
}

const AppTableCellComponent = <TData, TValue>(
  props: DataTableDragCellProps<TData, TValue>,
) => {
  const {
    cell,
    className,
    style = {},
    // classNameCell, onRowClick, onDoubleClick, styleCell
  } = props;

  const { isResizable, onClickRow, onDoubleClickRow } =
    useAppTableContext<TData>();

  const isGrouped = useMemo(() => cell.getIsGrouped(), [cell.getIsGrouped]);
  const isSelected = cell.row.getIsSelected();

  //TODO: Remove using meta and use columnDef.meta
  const isServiceColumn =
    cell.column.id === 'select' || cell.column.id === 'actions';

  const styles = useMemo(
    () => ({
      ...getCommonPinningStyles({
        column: cell.column,
        isServiceColumn,
        isResizable,
        isSelected,
      }),
      ...(isServiceColumn && {
        width: 40,
        maxSize: 40,
      }),
      ...style,
    }),
    [cell.column, isServiceColumn, isResizable, isSelected, style],
  );

  const handleClickRow = () => {
    if (!isServiceColumn && !cell.column.columnDef.meta?.isServiceColumn) {
      onClickRow?.(cell.row.original);
    }
  };

  const handleDoubleClickRow = () => {
    if (!isServiceColumn && !cell.column.columnDef.meta?.isServiceColumn) {
      onDoubleClickRow?.(cell.row.original);
    }
  };

  return (
    <TableCell
      key={cell.id}
      className={cn(
        'flex items-center overflow-hidden text-ellipsis text-xs whitespace-nowrap [&:not(:last-child)]:border-r py-0',
        className,
      )}
      style={styles}
      onClick={handleClickRow}
      onDoubleClick={handleDoubleClickRow}
    >
      {isGrouped ? (
        <>
          <button
            className="flex flex-row items-center gap-2"
            onClick={cell.row.getToggleExpandedHandler()}
          >
            {cell.row.getIsExpanded() ? (
              <ChevronDownIcon className="size-4" />
            ) : (
              <ChevronRightIcon className="size-4" />
            )}{' '}
            {flexRender(cell.column.columnDef.cell, cell.getContext())} (
            {cell.row.subRows.length})
          </button>
        </>
      ) : cell.getIsPlaceholder() ? null : (
        flexRender(cell.column.columnDef.cell, cell.getContext())
      )}
    </TableCell>
  );
};

export const AppTableCell = AppTableCellComponent;
